import { useTranslation } from 'react-i18next';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio';
import { Label } from '@components/ui/form/label';
import { SchedulerSidebarCrewChangeScope } from '@contexts/scheduler/sidebar/types';
import { useEffect, useState } from 'react';
import { cn } from '@utils/cn';
import { Button } from '@components/ui/button';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { useAppContext } from '@contexts/app';
import { getChangeScopeLabelParams } from './helpers';
import { SchedulerSidebarDays } from '../days';

export const SchedulerSidebarChanges = () => {
  const {
    crewChangeScope,
    disabled,
    setState: setSchedulerSidebarState,
    relatedMultischedules,
    currentSchedule,
    hasScheduleChanges,
  } = useSchedulerSidebar();
  const { tenant } = useAppContext();

  const { t } = useTranslation('app', { keyPrefix: 'schedule.changes' });

  const [expanded, setExpanded] = useState(true);

  const labelParams = relatedMultischedules
    ? getChangeScopeLabelParams({ relatedMultischedules, currentSchedule, zone: tenant.zone })
    : null;
  const hideFollowingDays = labelParams?.currentAndFuture.count === 0;

  useEffect(() => {
    if (!hideFollowingDays || crewChangeScope !== 'currentAndFuture') return;
    setSchedulerSidebarState((prev) => ({ ...prev, crewChangeScope: 'currentOnly' }));
  }, [hideFollowingDays, crewChangeScope, setSchedulerSidebarState]);

  if (!hasScheduleChanges) return null;
  if (!relatedMultischedules || !Object.keys(relatedMultischedules).length) return null;

  const optionLabel = (scope: SchedulerSidebarCrewChangeScope) =>
    labelParams ? t(`dates.${scope}`, labelParams[scope]) : t(`options.${scope}`);

  const renderDrawerContent = () => {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-xs font-normal text-neutral-500">{t('message')}</p>
        <RadioGroup
          value={crewChangeScope}
          onValueChange={(v) => {
            setSchedulerSidebarState((prev) => ({
              ...prev,
              crewChangeScope: v as SchedulerSidebarCrewChangeScope,
            }));
          }}
          className="flex flex-col gap-2 p-2">
          <div className="flex items-center gap-2">
            <RadioGroupItem value="currentOnly" id="crew-current-only" />
            <Label htmlFor="crew-current-only" className="cursor-pointer text-xs font-semibold">
              {optionLabel('currentOnly')}
            </Label>
          </div>
          {!hideFollowingDays && (
            <div className="flex items-center gap-2">
              <RadioGroupItem value="currentAndFuture" id="crew-current-and-future" />
              <Label
                htmlFor="crew-current-and-future"
                className="cursor-pointer text-xs font-semibold">
                {optionLabel('currentAndFuture')}
              </Label>
            </div>
          )}
          <div className="flex items-center gap-2">
            <RadioGroupItem value="all" id="crew-all" />
            <Label htmlFor="crew-all" className="cursor-pointer text-xs font-semibold">
              {optionLabel('all')}
            </Label>
          </div>
        </RadioGroup>
        {crewChangeScope !== 'currentOnly' && <SchedulerSidebarDays />}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2 border-t p-2',
        disabled && 'opacity-50 bg-neutral-50 pointer-events-none',
      )}>
      <div
        className="flex items-center gap-2 justify-between cursor-pointer"
        onClick={() => {
          setExpanded(!expanded);
        }}>
        <div className="flex items-center gap-2 justify-between w-full">
          <strong className="font-semibold text-xs">{t('title')}</strong>
          {!expanded && (
            <span className="text-xs font-semibold text-primary-500">
              {optionLabel(crewChangeScope)}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          className="h-fit gap-2 px-0 py-2 text-neutral-400 hover:bg-transparent hover:text-neutral-700"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}>
          <SvgConsumer id="expand" className={cn('shrink-0 text-xs', !expanded && 'rotate-180')} />
        </Button>
      </div>
      {expanded ? renderDrawerContent() : null}
    </div>
  );
};
